import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { BookOpen, RefreshCw, Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { quantWeeklyReviewLatest, quantWeeklyReviewRunNow, getErrorMessage } from '../../lib/api';

/**
 * WeeklyReviewPreview — Card header QuantLabPage.
 * Mostra path + modified_at + preview (max 500 char).
 * Bottone "Rilancia ora" → POST run-now con loading state + toast.
 */

const MAX_PREVIEW = 500;

const WeeklyReviewPreview = () => {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const r = await quantWeeklyReviewLatest();
      setReview(r.data);
    } catch (e) {
      // 404 = nessuna review ancora — non è un errore
      if (e.response?.status !== 404) {
        toast.error(getErrorMessage(e, 'Errore weekly review'));
      }
      setReview({ available: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const handleRunNow = async () => {
    setRunning(true);
    const tid = toast.loading('Generazione weekly review in corso...', {
      description: 'Può richiedere 20-60s.',
      duration: Infinity,
    });
    try {
      const r = await quantWeeklyReviewRunNow();
      const score = r.data?.validation?.score;
      toast.success('Weekly review generata', {
        id: tid,
        description: `Score ${score ?? '?'}/100 · ${(r.data?.saved_paths || []).join(', ')}`,
        duration: 8000,
      });
      // Ricarica preview
      await fetchLatest();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore generazione review'), { id: tid });
    } finally {
      setRunning(false);
    }
  };

  // Non disponibile
  if (!loading && (!review || !review.available)) {
    return (
      <Card className="border-dashed bg-muted/20">
        <CardContent className="py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BookOpen className="w-4 h-4 shrink-0" />
            Nessuna weekly review ancora. Genera la prima.
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={handleRunNow}
            disabled={running}
            aria-label="Genera prima weekly review AI"
          >
            {running ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> In corso...</>
            ) : (
              <><Play className="w-3.5 h-3.5 mr-1" /> Genera ora</>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const preview = review?.content_preview || '';
  const truncated = preview.length > MAX_PREVIEW;
  const displayText = truncated ? preview.slice(0, MAX_PREVIEW) + '...' : preview;

  const modifiedAt = review?.modified_at
    ? new Date(review.modified_at).toLocaleString('it-IT', {
        day: '2-digit', month: 'short', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <BookOpen className={`w-4 h-4 ${loading ? 'animate-pulse' : 'text-primary'}`} />
            Weekly Review
            {modifiedAt && (
              <span className="text-[10px] text-muted-foreground font-normal">
                · aggiornata {modifiedAt}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchLatest} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={handleRunNow}
              disabled={running}
              aria-label="Rilancia weekly review AI ora"
            >
              {running ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> In corso...</>
              ) : (
                <><Play className="w-3 h-3 mr-1" /> Rilancia ora</>
              )}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-0 space-y-2">
        {loading ? (
          <div className="h-12 bg-muted/40 animate-pulse rounded" />
        ) : (
          <>
            {/* Path file */}
            {review?.path && (
              <p className="text-[10px] font-mono text-muted-foreground truncate" title={review.path}>
                {review.path}
              </p>
            )}
            {/* Preview */}
            {displayText && (
              <div className="text-xs text-muted-foreground bg-muted/30 rounded p-3 whitespace-pre-wrap leading-relaxed max-h-28 overflow-auto">
                {displayText}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyReviewPreview;
